import React, { useEffect, useRef, useState } from "react";
import { fetchFreshserviceExportStatus, startFreshserviceExport, downloadFreshserviceExport } from "../utils/api";
import { fmtInstant } from "../utils/dates";

const ACTIVE = ["preparing", "waiting"];
const FAILURE = {
  request_ceiling: "The request limit was reached.", page_ceiling: "The page limit was reached before collection finished.",
  age_ceiling: "Collection reached its 24-hour limit.", page_retry_ceiling: "Freshservice could not return a page after three attempts.",
  upstream_rejected: "Freshservice rejected the collection request.",
  repeated_page: "Freshservice repeated a page.", conflicting_evidence: "Ticket observations conflict.",
  invalid_ticket: "Freshservice returned incomplete ticket facts.", mapping_changed: "The team mapping changed during collection.",
  size_ceiling: "The export storage limit was reached.",
};

export default function FreshserviceExportControl({ theme = {} }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(false);
  const mounted = useRef(false);
  const locked = useRef(false);
  const initiated = useRef(null);
  const revision = useRef(0);
  const colors = { muted: "#656b73", blue: "#1767a6", border: "#c8cdd2", red: "#b42318", ...theme };
  const active = ACTIVE.includes(status?.state);
  const available = status && ["not_requested", "preparing", "waiting", "ready", "failed"].includes(status.state);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    fetchFreshserviceExportStatus().then(value => {
      if (!cancelled) setStatus(value);
    }).catch(() => {
      if (!cancelled) setStatus({ state: "unavailable", downloadable: false });
    });
    return () => { cancelled = true; mounted.current = false; initiated.current = null; revision.current += 1; };
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    let timer;
    const poll = async () => {
      const version = revision.current;
      try {
        const next = await fetchFreshserviceExportStatus();
        if (!cancelled && version === revision.current) { setStatus(next); setError(""); }
      } catch {
        if (!cancelled) setError("Status is temporarily unavailable. Collection may still be running.");
      }
      if (!cancelled) timer = setTimeout(poll, 5000);
    };
    timer = setTimeout(poll, 5000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [active, status?.job_id]);

  async function download() {
    const blob = await downloadFreshserviceExport();
    if (!mounted.current) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "freshservice-export.json";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    if (status?.state !== "ready" || !status.downloadable || initiated.current !== status.job_id) return;
    initiated.current = null;
    download().catch(() => {
      if (mounted.current) setError("Download did not finish. Select Download Freshservice to try again.");
    });
    // Completion belongs to the initiating mounted view only.
  }, [status]);

  async function act(refresh = false, confirmed = false) {
    if (locked.current || !available) return;
    if (refresh && status.state === "failed" && !confirmed) { setConfirm(true); return; }
    locked.current = true;
    revision.current += 1;
    setBusy(true);
    setError("");
    setConfirm(false);
    try {
      if (!refresh && status.downloadable) {
        await download();
      } else {
        const next = await startFreshserviceExport({ refresh, confirmed });
        if (!mounted.current) return;
        if (ACTIVE.includes(next.state)) initiated.current = next.job_id;
        setStatus(next);
        if (next.cooldown) setError(`Refresh is available after ${fmtInstant(next.refresh_allowed_at, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}.`);
        if (!refresh && next.state === "ready" && next.downloadable) await download();
      }
    } catch {
      if (mounted.current) setError("Freshservice action unavailable. Check status before trying again.");
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  const button = { padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600,
    fontFamily: "inherit", border: `1px solid ${colors.border}`, background: "transparent",
    color: colors.blue, minHeight: 30, cursor: busy ? "wait" : "pointer" };
  return <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, maxWidth: "100%", minWidth: 0 }}>
    <button style={button} onClick={() => act()} disabled={busy || !available || (status?.state === "failed" && !status.downloadable)}>
      Download Freshservice
    </button>
    <button style={{ ...button, width: 32, height: 32, padding: 0, fontSize: 18, flexShrink: 0 }}
      aria-label="Refresh Freshservice data" title="Refresh Freshservice data" disabled={busy || !available}
      onClick={() => act(true)}>{"\u21bb"}</button>
    <span role="status" style={{ fontSize: 11, color: colors.muted, overflowWrap: "anywhere" }}>
      {!status ? "Checking Freshservice..." : ["disabled", "not_configured"].includes(status.state) ? "Freshservice export is not enabled."
        : status.state === "unavailable" ? "Freshservice export is unavailable."
          : active ? `${status.state === "waiting" ? "Waiting for Freshservice" : "Preparing"}: ${status.pages_completed || 0} pages collected; ${status.requests_reserved || 0}/${status.limits?.requests_per_job || 600} requests reserved. Page ceiling ${status.limits?.pages_per_job || 300}.`
            : status.state === "ready" ? "Ready. Coverage remains unverified."
              : status.state === "failed" ? FAILURE[status.error] || "Collection could not finish."
                : "No export prepared."}
    </span>
    {status?.downloadable && <span data-testid="freshservice-export-dates" style={{ fontSize: 11, color: colors.muted }}>
      {active || status.state === "failed" ? "Previous download" : "Generated"}: {fmtInstant(status.generated_at, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
      {status.state === "ready" && status.cutoff ? `; requested cutoff ${fmtInstant(status.cutoff, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}
    </span>}
    {confirm && <div role="alertdialog" aria-label="Confirm Freshservice refresh" style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
      <span style={{ fontSize: 12 }}>Start a new collection after this failure? Any available previous download will remain available.</span>
      <button style={button} onClick={() => act(true, true)}>Confirm refresh</button>
      <button style={button} onClick={() => setConfirm(false)}>Cancel</button>
    </div>}
    {error && <span role="alert" style={{ fontSize: 11, color: colors.red }}>{error}</span>}
  </div>;
}
